import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import FableticsModal from '~/shared/components/FableticsModal';
import {
  Dropdown as BaseDropdown,
  useModelImageContext,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import MemberModelButton from '../EnhancedMemberModelButton';
import EnhancedMemberModelItem from '../EnhancedMemberModelItem';
import { useMemberModelContext } from '../EnhancedMemberModelProvider';

const Dropdown = styled(BaseDropdown)`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.content.dropdown.base}
`;

const DropdownList = styled(Dropdown.List)`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.content.dropdown.list}
`;

const ContentWrapper = styled.div`
  ${({ theme: { pdpStyles } }) => pdpStyles.enhancedMemberModel.content.wrapper}
`;

const MemberModelSelectionLabelWrapper = styled.div`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.content.selectionLabel.wrapper}
`;

const MemberModelSelectionLabel = styled.span`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.content.selectionLabel.label}
`;

const modalWrapperStyle = css`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.content.modal.wrapper}
`;

const modalDialogStyle = css`
  ${({ theme: { pdpStyles } }) =>
    pdpStyles.enhancedMemberModel.content.modal.dialog}
`;

const DesktopWrapper = ({ children }) => {
  return <DropdownList>{children}</DropdownList>;
};
const MobileWrapper = ({ children }) => {
  const { closeModal, isModalOpen } = useMemberModelContext();

  const modalTransition = {
    from: {
      opacity: 0,
      transform: 'translateY(100%)',
      transition: `transform 20ms`,
    },
    enter: {
      opacity: 1,
      transform: 'translateY(0%)',
    },
    leave: {
      opacity: 0,
      transform: 'translateY(100%)',
    },
  };

  return (
    <FableticsModal
      defaultOpen={false}
      title="Member Model"
      isOpen={isModalOpen}
      onExit={closeModal}
      dialogStyle={modalDialogStyle}
      wrapperStyle={modalWrapperStyle}
      transitionEnter={modalTransition.enter}
      transitionFrom={modalTransition.from}
      transitionLeave={modalTransition.leave}
    >
      {children}
    </FableticsModal>
  );
};

export default function MemberModelContent() {
  const { closeModal, openModal } = useMemberModelContext();
  const { modelSets: memberModels, activeModelIndex } = useModelImageContext();
  const { isMobile } = useBreakpoint();
  const getProductImageSources = (imageSource) => [
    { images: imageSource.sizes },
  ];

  if (!memberModels.length > 0) {
    return null;
  }

  const ContentContainer = isMobile ? MobileWrapper : DesktopWrapper;
  const formattedMemberModels = memberModels.map((model) => {
    const formattedImages =
      model?.images.map((image) => getProductImageSources(image)) ?? [];
    return { ...model, imageSources: formattedImages };
  });

  const formattedOptions = memberModels.map((model) => {
    return {
      label: model?.model?.name || '',
      value: model?.model?.id,
    };
  });

  return (
    <Dropdown
      onClose={closeModal}
      onOpen={openModal}
      options={formattedOptions}
      value={activeModelIndex}
    >
      <MemberModelButton />
      <ContentContainer>
        <ContentWrapper data-testid="pdp-member-model-content">
          <MemberModelSelectionLabelWrapper>
            <MemberModelSelectionLabel>
              <FormattedMessage
                id="pdp_change_model.change_model"
                defaultMessage="Change Model"
              />
            </MemberModelSelectionLabel>
          </MemberModelSelectionLabelWrapper>
          {formattedMemberModels.map((memberModel, index) => {
            if (!memberModel.model) {
              return null;
            }
            return (
              <EnhancedMemberModelItem
                memberModel={memberModel}
                currentModelIndex={index}
                key={memberModel?.model?.id}
              />
            );
          })}
        </ContentWrapper>
      </ContentContainer>
    </Dropdown>
  );
}

DesktopWrapper.propTypes = {
  children: PropTypes.node,
};
MobileWrapper.propTypes = {
  children: PropTypes.node,
};
